import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TabHeader } from "@/components/royal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, ArrowUpDown, GripVertical, Shield, Anchor } from "lucide-react";

const STANDARD_UNITS = {
  "Light Infantry": { attack: 20, defense: 30, gold_cost: 30, resource_cost: 10 },
  "Archer": { attack: 30, defense: 10, gold_cost: 30, resource_cost: 10 },
  "Heavy Infantry": { attack: 30, defense: 40, gold_cost: 40, resource_cost: 10 },
  "Light Cavalry": { attack: 40, defense: 20, gold_cost: 50, resource_cost: 20 },
  "Heavy Cavalry": { attack: 50, defense: 40, gold_cost: 70, resource_cost: 30 },
};

const COLS = [
  { key: "unit", label: "Unit" },
  { key: "code", label: "Code" },
  { key: "location", label: "Location" },
  { key: "attack", label: "Attack", num: true },
  { key: "defense", label: "Defense", num: true },
  { key: "gold_cost", label: "Gold Cost", num: true },
  { key: "resource_cost", label: "Resource Cost", num: true },
];

const blank = () => ({ id: crypto.randomUUID(), unit: "", code: "", location: "", attack: 0, defense: 0, gold_cost: 0, resource_cost: 0 });

function UnitTable({ Icon, title, prefix, units, editable, onReorder, standard }) {
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [dialog, setDialog] = useState(null);
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const cmp = (key, dir) => (a, b) => {
    const va = a[key], vb = b[key];
    if (typeof va === "number") return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  };

  // Admin always sees manual array order (so drag works); sorting persists into the array.
  const displayed = editable ? units : (sort.key ? [...units].sort(cmp(sort.key, sort.dir)) : units);

  const headerClick = (key) => {
    if (editable) {
      const dir = sort.key === key ? -sort.dir : 1;
      onReorder([...units].sort(cmp(key, dir)));
      setSort({ key, dir });
    } else {
      setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
    }
  };

  const onDrop = (toIdx) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setDragOver(null);
    if (from == null || from === toIdx) return;
    const arr = [...units];
    const [moved] = arr.splice(from, 1);
    arr.splice(toIdx, 0, moved);
    onReorder(arr);
  };

  const totals = units.reduce((acc, u) => ({ gold: acc.gold + (u.gold_cost || 0), res: acc.res + (u.resource_cost || 0) }), { gold: 0, res: 0 });

  const openStandard = () => setDialog({ mode: "standard", draft: { ...blank(), unit: "Light Infantry", ...STANDARD_UNITS["Light Infantry"] } });
  const openCustom = () => setDialog({ mode: "custom", draft: blank() });
  const openEdit = (u) => setDialog({ mode: "edit", draft: { ...u } });
  const setDraft = (k, v) => setDialog((d) => ({ ...d, draft: { ...d.draft, [k]: v } }));
  const pickTemplate = (name) => setDialog((d) => ({ ...d, draft: { ...d.draft, unit: name, ...STANDARD_UNITS[name] } }));
  const save = () => {
    const draft = dialog.draft;
    const arr = [...units];
    if (dialog.mode === "edit") {
      const idx = arr.findIndex((x) => x.id === draft.id);
      if (idx >= 0) arr[idx] = draft;
    } else arr.push(draft);
    onReorder(arr);
    setDialog(null);
  };
  const remove = (id) => onReorder(units.filter((x) => x.id !== id));

  const colCount = COLS.length + (editable ? 2 : 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="ht-title text-2xl flex items-center gap-2">
          <Icon strokeWidth={1.5} className="w-6 h-6 text-gold" /> {title}
        </h3>
        {editable && (
          <div className="flex gap-2">
            {standard && (
              <Button variant="outline" size="sm" className="rounded-sm" onClick={openStandard} data-testid={`${prefix}-add-standard`}>
                <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Standard Unit
              </Button>
            )}
            <Button size="sm" className="rounded-sm" onClick={openCustom} data-testid={`${prefix}-add-custom`}>
              <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> {standard ? "Custom Unit" : "Add Ship"}
            </Button>
          </div>
        )}
      </div>

      {editable && <p className="text-xs text-muted-foreground">Drag the <GripVertical className="inline w-3.5 h-3.5" /> handle to reorder rows. Click a column header to sort.</p>}

      <div className="ht-panel overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${prefix}-table`}>
          <thead>
            <tr className="border-b border-border bg-card">
              {editable && <th className="w-8 px-2 py-3" />}
              {COLS.map((c) => (
                <th key={c.key} className="text-left px-4 py-3">
                  <button className="flex items-center gap-1 ht-uppercase-label hover:text-foreground" onClick={() => headerClick(c.key)} data-testid={`${prefix}-sort-${c.key}`}>
                    {c.label} <ArrowUpDown strokeWidth={1.5} className="w-3 h-3 opacity-50" />
                  </button>
                </th>
              ))}
              {editable && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-muted-foreground">No units.</td></tr>
            ) : displayed.map((u, i) => (
              <tr
                key={u.id}
                data-testid={`${prefix}-row-${i}`}
                draggable={editable}
                onDragStart={() => { dragFrom.current = i; }}
                onDragOver={(e) => { if (editable) { e.preventDefault(); setDragOver(i); } }}
                onDrop={() => onDrop(i)}
                onDragEnd={() => { dragFrom.current = null; setDragOver(null); }}
                className={`border-b border-border last:border-0 hover:bg-secondary/50 ${dragOver === i ? "bg-gold/10" : ""} ${editable ? "cursor-grab active:cursor-grabbing" : ""}`}
              >
                {editable && (
                  <td className="px-2 py-3 text-center text-muted-foreground" data-testid={`${prefix}-drag-${i}`}>
                    <GripVertical strokeWidth={1.5} className="w-4 h-4 inline" />
                  </td>
                )}
                {COLS.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.num ? "tabular-nums" : ""}`}>{u[c.key]}</td>
                ))}
                {editable && (
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8" onClick={() => openEdit(u)} data-testid={`${prefix}-edit-${i}`}>
                      <Pencil strokeWidth={1.5} className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 text-destructive" onClick={() => remove(u.id)} data-testid={`${prefix}-delete-${i}`}>
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-card font-medium" data-testid={`${prefix}-totals`}>
              <td className="px-4 py-3 font-heading" colSpan={editable ? 6 : 5}>Total Cost</td>
              <td className="px-4 py-3 tabular-nums" data-testid={`${prefix}-total-gold`}>{totals.gold}</td>
              <td className="px-4 py-3 tabular-nums" data-testid={`${prefix}-total-resources`}>{totals.res}</td>
              {editable && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-sm bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog?.mode === "edit" ? "Edit Unit" : dialog?.mode === "standard" ? "Add Standard Unit" : `Add ${standard ? "Custom Unit" : "Ship"}`}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              {dialog.mode === "standard" ? (
                <div>
                  <label className="ht-uppercase-label block mb-1.5">Unit Type</label>
                  <Select value={dialog.draft.unit} onValueChange={pickTemplate}>
                    <SelectTrigger className="rounded-sm bg-background" data-testid={`${prefix}-standard-select`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STANDARD_UNITS).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <label className="ht-uppercase-label block mb-1.5">Unit Name</label>
                  <Input value={dialog.draft.unit} onChange={(e) => setDraft("unit", e.target.value)} className="rounded-sm bg-background" data-testid={`${prefix}-draft-unit`} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="ht-uppercase-label block mb-1.5">Code</label>
                  <Input value={dialog.draft.code} onChange={(e) => setDraft("code", e.target.value)} className="rounded-sm bg-background" data-testid={`${prefix}-draft-code`} /></div>
                <div><label className="ht-uppercase-label block mb-1.5">Location</label>
                  <Input value={dialog.draft.location} onChange={(e) => setDraft("location", e.target.value)} className="rounded-sm bg-background" data-testid={`${prefix}-draft-location`} /></div>
                {["attack", "defense", "gold_cost", "resource_cost"].map((k) => (
                  <div key={k}><label className="ht-uppercase-label block mb-1.5">{k.replace("_", " ")}</label>
                    <Input type="number" value={dialog.draft[k]} onChange={(e) => setDraft(k, Number(e.target.value))} className="rounded-sm bg-background tabular-nums" data-testid={`${prefix}-draft-${k}`} /></div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} className="rounded-sm w-full font-heading" data-testid={`${prefix}-save-unit`}>Save Unit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Military({ player, updatePlayer, editable }) {
  const setArmy = (arr) => updatePlayer((p) => { p.army = arr; });
  const setNavy = (arr) => updatePlayer((p) => { p.navy = arr; });

  return (
    <div className="space-y-10">
      <TabHeader label="Standing Forces" title="Military" />
      <UnitTable Icon={Shield} title="Army" prefix="army" units={player.army || []} editable={editable} onReorder={setArmy} standard />
      <UnitTable Icon={Anchor} title="Navy" prefix="navy" units={player.navy || []} editable={editable} onReorder={setNavy} standard={false} />
    </div>
  );
}
